import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import AiBadge from './AiBadge';
import './Dashboard.css';

const Dashboard = () => {
  const { userName } = useParams();
  const navigate = useNavigate();
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replacingPhoto, setReplacingPhoto] = useState({}); // { [photoId]: true/false }
  const [deletingPhoto, setDeletingPhoto] = useState({}); // { [photoId]: true/false }
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState('admin');
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRefs = useRef({});

  const fetchUserStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUserStatus(userName);
      setUserStatus(response);
    } catch (err) {
      console.error('Error fetching user status:', err);
      setError(err.response?.data?.error || 'Failed to fetch user status');
    } finally {
      setLoading(false);
    }
  }, [userName]);

  useEffect(() => {
    if (!userName || userName === 'undefined') {
      navigate('/login');
      return;
    }
    
    // Auth Check
    const sessionUserName = sessionStorage.getItem('userName');
    const isAdminToken = sessionStorage.getItem('adminToken');
    if (!isAdminToken && sessionUserName && sessionUserName !== userName) {
      navigate(`/dashboard/${encodeURIComponent(sessionUserName)}`);
      return;
    }

    if (isAdminToken) {
      setIsAdmin(true);
      const masterStatus = sessionStorage.getItem('isMasterAdmin');
      if (masterStatus === 'true' || masterStatus === true) {
        setIsMasterAdmin(true);
      }
      setAdminRole(sessionStorage.getItem('adminRole') || 'admin');
    }
    fetchUserStatus();
  }, [userName, fetchUserStatus, navigate]);

  const handleBackToUpload = () => {
    const uploadId = userStatus?.user?.name || userName;
    navigate(`/upload/${encodeURIComponent(uploadId)}`);
  };

  const handleReplacePhoto = async (photoId, file) => {
    if (!file) return;
    setReplacingPhoto(prev => ({ ...prev, [photoId]: true }));
    try {
      await apiService.replacePhoto(photoId, file);
      // Refresh dashboard data to show updated photo
      await fetchUserStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to replace photo. Please try again.');
    } finally {
      setReplacingPhoto(prev => ({ ...prev, [photoId]: false }));
      // Reset the file input so the same file can be selected again if needed
      if (fileInputRefs.current[photoId]) {
        fileInputRefs.current[photoId].value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    setDeletingPhoto(prev => ({ ...prev, [photoId]: true }));
    try {
      await apiService.deletePhoto(photoId);
      // Refresh dashboard data to remove deleted photo
      await fetchUserStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete photo.');
    } finally {
      setDeletingPhoto(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const openEditModal = () => {
    setEditFormData({
      name: userStatus.user.name,
      email: userStatus.user.email,
      mobile_no: userStatus.user.mobile_no,
      dob: userStatus.user.dob,
      android_version: userStatus.user.android_version,
      country: userStatus.user.country || '',
      gender: userStatus.user.gender 
        ? (userStatus.user.gender.toLowerCase() === 'boy' ? 'Male' 
           : userStatus.user.gender.toLowerCase() === 'girl' ? 'Female' 
           : userStatus.user.gender) 
        : '',
      ethnicity: userStatus.user.ethnicity || ''
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiService.updateUserProfile(userStatus.user.id, editFormData);
      setShowEditModal(false);

      // If the name changed, we should navigate to the new name URL
      if (editFormData.name !== userName) {
        navigate(`/dashboard/${encodeURIComponent(editFormData.name)}`);
      } else {
        await fetchUserStatus();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update profile.');
      setLoading(false);
    }
  };


  const downloadUserZip = async (userId) => {
    try {
      // 1. Fetch metadata
      const metadata = await apiService.getUserExportMetadata(userId);
      
      // 2. Format historic capture dates to be just the date with dashes
      if (metadata && metadata.historic_capture_dates) {
        Object.keys(metadata.historic_capture_dates).forEach(key => {
          let dateStr = metadata.historic_capture_dates[key];
          if (dateStr && typeof dateStr === 'string') {
            metadata.historic_capture_dates[key] = dateStr.split(' ')[0].replace(/:/g, '-');
          }
        });
        
        // 3. Save formatted metadata
        await apiService.updateUserExportMetadata(userId, metadata);
      }

      // 4. Download zip
      const blob = await apiService.exportUserData(userId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_${userId}_data.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Failed to download ZIP:', err);
      alert('Failed to download ZIP automatically.');
    }
  };

  const handleAdminApprove = async () => {
    if (!window.confirm("Are you sure you want to forcefully approve this user's photos?")) return;
    try {
      setLoading(true);
      await apiService.forceApproveUser(userStatus.user.id);
      await fetchUserStatus();
      alert("Success! User has been forcefully approved. Generating ZIP file...");
      await downloadUserZip(userStatus.user.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to force approve photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNormalApprove = async () => {
    if (!window.confirm("Are you sure you want to run the normal approval process?")) return;
    try {
      setLoading(true);
      await apiService.approvePhotos(userStatus.user.id);
      await fetchUserStatus();
      alert("Success! User has been approved. Generating ZIP file...");
      await downloadUserZip(userStatus.user.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPose = async (photoId, pose) => {
    try {
      await apiService.overridePhotoPose(photoId, pose);
      await fetchUserStatus(); // Refresh the data to show the new pose
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign pose.');
    }
  };

  const handleDownloadImage = async (e, url, filename) => {
    e.preventDefault();
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'download.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed', err);
      window.open(url, '_blank');
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return (
      <span>
        {d.toLocaleDateString()}<br />{d.toLocaleTimeString()}
      </span>
    );
  };

  const formatExifDate = (exifStr) => {
    if (!exifStr || typeof exifStr !== 'string') return 'Missing';
    const parts = exifStr.split(' ');
    if (parts.length !== 2) return exifStr;

    const datePart = parts[0].replace(/:/g, '-');
    const timeParts = parts[1].split(':');
    if (timeParts.length < 2) return exifStr;

    let hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    const seconds = timeParts[2] || '00';

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return (
      <span>
        {datePart}<br />{hours}:{minutes}:{seconds} {ampm}
      </span>
    );
  };

  const formatDOB = (dobStr) => {
    if (!dobStr) return '';
    const parts = dobStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
    }
    return dobStr;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/')} className="back-btn">
          Go to Registration
        </button>
      </div>
    );
  }

  if (!userStatus) {
    return (
      <div className="error-container">
        <div className="error-message">No data available</div>
        <button onClick={() => navigate('/')} className="back-btn">
          Go to Registration
        </button>
      </div>
    );
  }

  const postAValid = userStatus.post_a_status?.photos?.some(p => p.is_valid || p.validation_results?.is_valid);
  const postBValid = userStatus.post_b_status?.photos?.some(p => p.is_valid || p.validation_results?.is_valid);

  const isAssignedToPostB = userStatus.user.id % 2 === 0;

  const isAllComplete = userStatus.old_photo_status.valid &&
    userStatus.present_photos_status.all_valid &&
    (isAssignedToPostB ? postBValid : postAValid);
  const isApproved = userStatus.user.is_approved;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>📊 User Dashboard {isApproved && <span className="approved-badge">✓ APPROVED</span>}</h2>
        <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
          {adminRole !== 'viewer' && (
            <button onClick={openEditModal} className="view-btn">
              ✏️ Edit Profile
            </button>
          )}
          {!isApproved && adminRole !== 'viewer' && (
            <button onClick={handleBackToUpload} className="upload-btn">
              📸 Back to Upload
            </button>
          )}
        </div>
      </div>

      {isAdmin && adminRole !== 'viewer' && !isApproved && (
        <div className="admin-force-approve-section" style={{ background: '#fef3c7', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #f59e0b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#b45309' }}>⚡ Admin Override</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>Approve this user's account using the normal process or forcefully bypass validation.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleNormalApprove} className="upload-btn" style={{ background: '#10b981', borderColor: '#10b981', margin: 0, whiteSpace: 'nowrap' }}>
              ✅ Normal Approve
            </button>
            <button onClick={handleAdminApprove} className="upload-btn" style={{ background: '#f59e0b', borderColor: '#f59e0b', margin: 0, whiteSpace: 'nowrap' }}>
              ⚡ Force Approve
            </button>
          </div>
        </div>
      )}

      <div className="user-info">
        <h3>👤 User Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Name:</label>
            <span>{userStatus.user.name}</span>
          </div>
          {adminRole !== 'viewer' && (
            <>
              <div className="info-item">
                <label>Email:</label>
                <span>{userStatus.user.email}</span>
              </div>
              <div className="info-item">
                <label>Mobile:</label>
                <span>{userStatus.user.mobile_no}</span>
              </div>
            </>
          )}
          <div className="info-item">
            <label>Date of Birth:</label>
            <span>{formatDOB(userStatus.user.dob)}</span>
          </div>
          <div className="info-item">
            <label>Android Version:</label>
            <span>{userStatus.user.android_version}</span>
          </div>
          <div className="info-item">
            <label>Country:</label>
            <span>{userStatus.user.country || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Gender:</label>
            <span>
              {userStatus.user.gender
                ? (userStatus.user.gender.toLowerCase() === 'boy' ? 'Male'
                   : userStatus.user.gender.toLowerCase() === 'girl' ? 'Female'
                   : userStatus.user.gender)
                : 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <label>Ethnicity:</label>
            <span>{userStatus.user.ethnicity || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Registered:</label>
            <span>{formatDate(userStatus.user.created_at)}</span>
          </div>
        </div>
      </div>

      <div className="photo-status">
        <h3>📸 Photo Verification Status</h3>

        <div className="status-section">
          <h4>📷 Old Photos</h4>
          <div className={`status-indicator ${userStatus.old_photo_status.valid ? 'valid' : 'invalid'}`}>
            {userStatus.old_photo_status.uploaded ? (
              userStatus.old_photo_status.valid ?
                '✅ Valid old photo uploaded' :
                '❌ Old photo uploaded but invalid'
            ) : (
              '⏳ No old photo uploaded'
            )}
          </div>

          <div className="present-photos-grid">
            {userStatus.old_photo_status.photos.map((photo, index) => {
              const val = photo.validation_results || {};
              const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
              const isValid = photo.is_valid || val.is_valid;
              const reasons = val.rejection_reasons || [];

              return (
                <div key={photo.id} className={`photo-details ${isValid ? 'valid-item' : 'invalid-item'}`}>
                  <div className="photo-media-wrapper">
                    <AiBadge
                      score={photo.ai_confidence || val.ai_confidence}
                      signals={val.ai_signals || photo.ai_signals}
                      stage="Historical"
                      isAdmin={isAdmin}
                    />
                    <img
                      src={photoUrl}
                      alt={`Old upload ${index + 1}`}
                      className="dashboard-thumbnail"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setLightboxImage(photoUrl)}
                      onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"; }}
                    />
                    <span className={`pill-badge ${isValid ? 'pill-ok' : 'pill-err'}`}>
                      {isValid ? '✅ Accepted' : '❌ Rejected'}
                    </span>
                  </div>
                  <div className="photo-info-content">
                    <h5>{photo.custom_filename || `Old Photo ${index + 1}`}</h5>
                    <p className="upload-time" style={{ marginTop: 0, marginBottom: '0.2rem' }}>📅 Uploaded: {formatDate(photo.uploaded_at)}</p>
                    <br />
                    <p className="upload-time" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                      📸 EXIF Date: <strong style={{ color: val.exif_date ? '#10b981' : '#dc2626' }}>{val.exif_date ? formatExifDate(val.exif_date) : 'Missing'}</strong>
                    </p>
                    <div className="validation-details">
                      {isAdmin && (
                        <div className="validation-grid">
                          <div className="validation-item">
                            <span>File size &gt;= 128KB:</span>
                            <span className={val.file_size_valid ? 'success' : 'error'}>
                              {val.file_size_valid ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>AI Detection:</span>
                            <span className={val.is_ai_generated ? 'error' : 'success'}>
                              {val.is_ai_generated ? `⚠️ AI (${val.ai_confidence}%)` : '✓ Real'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>Subjects detected:</span>
                            <span className={val.subjects_detected > 0 ? 'success' : 'error'}>
                              {val.subjects_detected}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>Eyes visible:</span>
                            <span className={val.eyes_visible ? 'success' : 'error'}>
                              {val.eyes_visible ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="validation-item">
                            {/* <span>EXIF Date:</span>
                            <span className={val.exif_date ? 'success' : 'error'}>
                              {val.exif_date ?`✓ ${val.exif_date}` : '✗ Missing'}
                            </span> */}
                          </div>
                        </div>
                      )}
                      {!isValid && reasons.length > 0 && (
                        <div className="reasons-snippet">
                          <strong>Reason:</strong> {reasons.join(', ')}
                        </div>
                      )}

                      {adminRole !== 'viewer' && (
                        <>
                          <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                            <button
                              onClick={(e) => handleDownloadImage(e, photoUrl, photo.custom_filename || `old_photo_${index + 1}.jpg`)}
                              className="replace-btn"
                              title="Download image"
                              style={{ backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >⬇️ Download</button>
                          </div>

                          {!isApproved && (
                            <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                ref={el => fileInputRefs.current[photo.id] = el}
                                onChange={(e) => handleReplacePhoto(photo.id, e.target.files[0])}
                              />
                              <button
                                className="replace-btn"
                                disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                onClick={() => fileInputRefs.current[photo.id]?.click()}
                              >
                                {replacingPhoto[photo.id] ? '⏳ Replacing...' : '🔄 Replace Photo'}
                              </button>
                              <button
                                className="replace-btn"
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                onClick={() => handleDeletePhoto(photo.id)}
                              >
                                {deletingPhoto[photo.id] ? '⏳...' : '🗑️ Delete'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="status-section">
          <h4>📱 Present Photos</h4>
          <div className={`status-indicator ${userStatus.present_photos_status.all_valid ? 'valid' : 'invalid'}`}>
            📊 {userStatus.present_photos_status.uploaded_count} / {userStatus.present_photos_status.required_count} photos uploaded
            ({userStatus.present_photos_status.without_glasses_count} without glasses)
          </div>

          {userStatus.present_photos_status.all_uploaded && (
            <div className={`completion-status ${userStatus.present_photos_status.all_valid ? 'complete' : 'incomplete'}`}>
              {userStatus.present_photos_status.all_valid ?
                '✅ Requirement met (All valid, 3+ photos without glasses)' :
                '❌ Some present photos need attention or missing'
              }
            </div>
          )}

          <div className="present-photos-grid">
            {userStatus.present_photos_status.photos.map((photo, index) => {
              const val = photo.validation_results || {};
              const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
              const isValid = photo.is_valid || val.is_valid;
              const reasons = val.rejection_reasons || [];

              return (
                <div key={photo.id} className={`photo-details ${isValid ? 'valid-item' : 'invalid-item'}`}>
                  <div className="photo-media-wrapper">
                    <AiBadge
                      score={photo.ai_confidence || val.ai_confidence}
                      signals={val.ai_signals || photo.ai_signals}
                      stage="Present_Neutral"
                      isAdmin={isAdmin}
                    />
                    <img
                      src={photoUrl}
                      alt={`Present upload ${index + 1}`}
                      className="dashboard-thumbnail"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setLightboxImage(photoUrl)}
                      onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"; }}
                    />
                    <span className={`pill-badge ${isValid ? 'pill-ok' : 'pill-err'}`}>
                      {isValid ? '✅ Accepted' : '❌ Rejected'}
                    </span>
                  </div>
                  <div className="photo-info-content">
                    <h5>Photo {index + 1}</h5>
                    <p className="upload-time" style={{ marginBottom: '0.2rem' }}>📅 Uploaded: {formatDate(photo.uploaded_at)}</p>
                    <p className="upload-time" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                      📸 EXIF Date: <strong style={{ color: val.exif_date ? '#10b981' : '#dc2626' }}>{val.exif_date ? formatExifDate(val.exif_date) : 'Missing'}</strong>
                    </p>
                    <div className="validation-details">
                      {isAdmin && (
                        <div className="validation-grid">
                          <div className="validation-item">
                            <span>File size &gt;= 128KB:</span>
                            <span className={val.file_size_valid !== false ? 'success' : 'error'}>
                              {val.file_size_valid !== false ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>AI Detection:</span>
                            <span className={val.is_ai_generated ? 'error' : 'success'}>
                              {val.is_ai_generated ? `⚠️ AI (${val.ai_confidence}%)` : '✓ Real'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>Front Facing:</span>
                            <span className={val.front_facing !== false ? 'success' : 'error'}>
                              {val.front_facing !== false ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>Shoulders:</span>
                            <span className={val.shoulders_visible !== false ? 'success' : 'error'}>
                              {val.shoulders_visible !== false ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="validation-item">
                            <span>Glasses:</span>
                            <span className={!val.has_glasses ? 'success' : 'warning'}>
                              {val.has_glasses ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="validation-item">
                            {/* <span>EXIF Date:</span>
                            <span className={val.exif_date ? 'success' : 'error'}>
                              {val.exif_date ? `✓ ${val.exif_date}` : '✗ Missing'}
                            </span> */}
                          </div>
                        </div>
                      )}
                      {!isValid && reasons.length > 0 && (
                        <div className="reasons-snippet">
                          <strong>Reason:</strong> {reasons.join(', ')}
                        </div>
                      )}

                      {adminRole !== 'viewer' && (
                        <>
                          <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                            <button
                              onClick={(e) => handleDownloadImage(e, photoUrl, `present_photo_${index + 1}.jpg`)}
                              className="replace-btn"
                              title="Download image"
                              style={{ backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >⬇️ Download</button>
                          </div>

                          {!isApproved && (
                            <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                ref={el => fileInputRefs.current[photo.id] = el}
                                onChange={(e) => handleReplacePhoto(photo.id, e.target.files[0])}
                              />
                              <button
                                className="replace-btn"
                                disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                onClick={() => fileInputRefs.current[photo.id]?.click()}
                              >
                                {replacingPhoto[photo.id] ? '⏳ Replacing...' : '🔄 Replace Photo'}
                              </button>
                              <button
                                className="replace-btn"
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                onClick={() => handleDeletePhoto(photo.id)}
                              >
                                {deletingPhoto[photo.id] ? '⏳...' : '🗑️ Delete'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* POST Variation A SECTION */}
        <div className="status-card">
          <div className="card-header">
            <h4>📁 Post Variation A</h4>
            <div className={`status-indicator ${postAValid ? 'valid' : 'invalid'}`}>
              {userStatus.post_a_status?.photos?.length > 0 ? (
                userStatus.post_a_status.photos.some(p => p.is_valid) ?
                  '✅ Valid Post Variation A photo uploaded' :
                  '❌ Post Variation A photo uploaded but invalid'
              ) : (
                '⏳ No Post Variation A photo uploaded'
              )}
            </div>

            {userStatus.post_a_status?.photos && userStatus.post_a_status.photos.length > 0 && (
              <div className="present-photos-grid">
                {userStatus.post_a_status.photos.map((photo, index) => {
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id} className="photo-details valid-item">
                      <div className="photo-media-wrapper">
                        <AiBadge
                          score={photo.ai_confidence || photo.validation_results?.ai_confidence}
                          signals={photo.validation_results?.ai_signals || photo.ai_signals}
                          stage="Pose_Variation_A"
                          isAdmin={isAdmin}
                        />
                        <img
                          src={photoUrl}
                          alt={`Post A upload ${index + 1}`}
                          className="dashboard-thumbnail"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setLightboxImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"; }}
                        />
                        <span className={`pill-badge ${photo.is_valid || photo.validation_results?.is_valid ? 'pill-ok' : 'pill-error'}`}>
                          {photo.is_valid || photo.validation_results?.is_valid ? '✅ Valid Pose' : '❌ Invalid'}
                        </span>
                      </div>
                      <div className="photo-info-content">
                        <h5>Variation A Photo {index + 1}</h5>
                        <p className="upload-time" style={{ marginBottom: '0.2rem' }}>📅 Uploaded: {formatDate(photo.uploaded_at)}</p>
                        <p className="upload-time" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                          📸 EXIF Date: <strong style={{ color: photo.validation_results?.exif_date ? '#10b981' : '#dc2626' }}>{photo.validation_results?.exif_date ? formatExifDate(photo.validation_results.exif_date) : 'Missing'}</strong>
                        </p>
                        {isAdmin && (
                          <>
                            <p className="pose-detected" style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>Pose:</strong> {photo.validation_results?.detected_pose ? (
                                photo.validation_results.detected_pose !== 'UNASSIGNED'
                                  ? `✓ ${photo.validation_results.detected_pose}`
                                  : '⏳ Pending Admin Review'
                              ) : '✗ Unknown'}
                            </p>
                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>AI Detection:</strong> <span style={{ color: (photo.validation_results?.is_ai_generated || photo.is_ai_generated) ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>
                                {(photo.validation_results?.is_ai_generated || photo.is_ai_generated) ? `⚠️ AI (${photo.validation_results?.ai_confidence || photo.ai_confidence}%)` : '✓ Real'}
                              </span>
                            </p>
                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              {/* <strong>EXIF Date:</strong> <span style={{ color: photo.validation_results?.exif_date ? '#10b981' : '#dc2626', fontWeight: 'bold' }}>
                                {photo.validation_results?.exif_date ? `✓ ${photo.validation_results.exif_date}` : '✗ Missing'}
                              </span> */}
                            </p>
                          </>
                        )}

                        {isAdmin && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Assign Pose:</label>
                            <select
                              value={photo.validation_results?.detected_pose !== 'UNASSIGNED' ? photo.validation_results?.detected_pose : ''}
                              onChange={(e) => handleAssignPose(photo.id, e.target.value)}
                              style={{ padding: '0.2rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            >
                              <option value="">-- Select Pose --</option>
                              <option value="UP">UP</option>
                              <option value="DOWN">DOWN</option>
                              <option value="LEFT">LEFT</option>
                              <option value="RIGHT">RIGHT</option>
                            </select>
                          </div>
                        )}

                        {!photo.is_valid && !photo.validation_results?.is_valid && photo.validation_results?.rejection_reasons?.length > 0 && (
                          <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0' }}>
                            {photo.validation_results.rejection_reasons[0]}
                          </p>
                        )}

                        {adminRole !== 'viewer' && (
                          <>
                            <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                              <button
                                onClick={(e) => handleDownloadImage(e, photoUrl, `post_a_photo_${index + 1}.jpg`)}
                                className="replace-btn"
                                title="Download image"
                                style={{ backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >⬇️ Download</button>
                            </div>

                            {!isApproved && (
                              <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  ref={el => fileInputRefs.current[photo.id] = el}
                                  onChange={(e) => handleReplacePhoto(photo.id, e.target.files[0])}
                                />
                                <button
                                  className="replace-btn"
                                  disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                  onClick={() => fileInputRefs.current[photo.id]?.click()}
                                >
                                  {replacingPhoto[photo.id] ? '⏳ Replacing...' : '🔄 Replace Photo'}
                                </button>
                                <button
                                  className="replace-btn"
                                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                  disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                  onClick={() => handleDeletePhoto(photo.id)}
                                >
                                  {deletingPhoto[photo.id] ? '⏳...' : '🗑️ Delete'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* POST Variation B SECTION */}
        <div className="status-card">
          <div className="card-header">
            <h4>📂 Post Variation B</h4>
            <div className={`status-indicator ${postBValid ? 'valid' : 'invalid'}`}>
              {userStatus.post_b_status?.photos?.length > 0 ? (
                userStatus.post_b_status.photos.some(p => p.is_valid) ?
                  '✅ Valid Post Variation B photo uploaded' :
                  '❌ Post Variation B photo uploaded but invalid'
              ) : (
                '⏳ No Post Variation B photo uploaded'
              )}
            </div>

            {userStatus.post_b_status?.photos && userStatus.post_b_status.photos.length > 0 && (
              <div className="present-photos-grid">
                {userStatus.post_b_status.photos.map((photo, index) => {
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id} className="photo-details valid-item">
                      <div className="photo-media-wrapper">
                        <AiBadge
                          score={photo.ai_confidence || photo.validation_results?.ai_confidence}
                          signals={photo.validation_results?.ai_signals || photo.ai_signals}
                          stage="Pose_Variation_B"
                          isAdmin={isAdmin}
                        />
                        <img
                          src={photoUrl}
                          alt={`Post B upload ${index + 1}`}
                          className="dashboard-thumbnail"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setLightboxImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"; }}
                        />
                        <span className={`pill-badge ${photo.is_valid || photo.validation_results?.is_valid ? 'pill-ok' : 'pill-error'}`}>
                          {photo.is_valid || photo.validation_results?.is_valid ? '✅ Valid Pose' : '❌ Invalid'}
                        </span>
                      </div>
                      <div className="photo-info-content">
                        <h5>Variation B Photo {index + 1}</h5>
                        <p className="upload-time" style={{ marginBottom: '0.2rem' }}>📅 Uploaded: {formatDate(photo.uploaded_at)}</p>
                        <p className="upload-time" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                          📸 EXIF Date: <strong style={{ color: photo.validation_results?.exif_date ? '#10b981' : '#dc2626' }}>{photo.validation_results?.exif_date ? formatExifDate(photo.validation_results.exif_date) : 'Missing'}</strong>
                        </p>
                        {isAdmin && (
                          <>
                            <p className="pose-detected" style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>Pose:</strong> {photo.validation_results?.detected_pose ? (
                                photo.validation_results.detected_pose !== 'UNASSIGNED'
                                  ? `✓ ${photo.validation_results.detected_pose}`
                                  : '⏳ Pending Admin Review'
                              ) : '✗ Unknown'}
                            </p>
                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>AI Detection:</strong> <span style={{ color: (photo.validation_results?.is_ai_generated || photo.is_ai_generated) ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>
                                {(photo.validation_results?.is_ai_generated || photo.is_ai_generated) ? `⚠️ AI (${photo.validation_results?.ai_confidence || photo.ai_confidence}%)` : '✓ Real'}
                              </span>
                            </p>
                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              {/* <strong>EXIF Date:</strong> <span style={{ color: photo.validation_results?.exif_date ? '#10b981' : '#dc2626', fontWeight: 'bold' }}>
                                {photo.validation_results?.exif_date ? `✓ ${photo.validation_results.exif_date}` : '✗ Missing'}
                              </span> */}
                            </p>
                          </>
                        )}

                        {isAdmin && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Assign Pose:</label>
                            <select
                              value={photo.validation_results?.detected_pose !== 'UNASSIGNED' ? photo.validation_results?.detected_pose : ''}
                              onChange={(e) => handleAssignPose(photo.id, e.target.value)}
                              style={{ padding: '0.2rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            >
                              <option value="">-- Select Pose --</option>
                              <option value="UP-LEFT">UP-LEFT</option>
                              <option value="UP-RIGHT">UP-RIGHT</option>
                              <option value="DOWN-LEFT">DOWN-LEFT</option>
                              <option value="DOWN-RIGHT">DOWN-RIGHT</option>
                            </select>
                          </div>
                        )}

                        {!photo.is_valid && !photo.validation_results?.is_valid && photo.validation_results?.rejection_reasons?.length > 0 && (
                          <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0' }}>
                            {photo.validation_results.rejection_reasons[0]}
                          </p>
                        )}

                        {adminRole !== 'viewer' && (
                          <>
                            <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                              <button
                                onClick={(e) => handleDownloadImage(e, photoUrl, `post_b_photo_${index + 1}.jpg`)}
                                className="replace-btn"
                                title="Download image"
                                style={{ backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >⬇️ Download</button>
                            </div>

                            {!isApproved && (
                              <div className="replace-action" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  ref={el => fileInputRefs.current[photo.id] = el}
                                  onChange={(e) => handleReplacePhoto(photo.id, e.target.files[0])}
                                />
                                <button
                                  className="replace-btn"
                                  disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                  onClick={() => fileInputRefs.current[photo.id]?.click()}
                                >
                                  {replacingPhoto[photo.id] ? '⏳ Replacing...' : '🔄 Replace Photo'}
                                </button>
                                <button
                                  className="replace-btn"
                                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                  disabled={replacingPhoto[photo.id] || deletingPhoto[photo.id]}
                                  onClick={() => handleDeletePhoto(photo.id)}
                                >
                                  {deletingPhoto[photo.id] ? '⏳...' : '🗑️ Delete'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      <div className="overall-status">
        <h3>🎯 Overall Verification Status</h3>

        {isApproved ? (
          <div className="status-complete">
            <div className="success-icon">🎖️</div>
            <p className="success">Photos Approved & Submitted!</p>
            <p className="success-message">Your photo verification process is complete. No further action is required.</p>
          </div>
        ) : isAllComplete ? (
          <div className="status-complete">
            <div className="success-icon">🎉</div>
            <p className="success">All requirements completed successfully!</p>
            <p className="success-message">Your photos are valid and ready for final submission.</p>
            {isAdmin ? (
              <button onClick={handleAdminApprove} className="upload-btn" style={{ fontSize: '1.1rem', padding: '0.8rem 2rem', backgroundColor: '#f59e0b' }}>
                👑 Admin: Approve User
              </button>
            ) : (
              <p className="success-message" style={{ fontWeight: 'bold' }}>Pending Admin Approval.</p>
            )}
          </div>
        ) : (
          <div className="status-incomplete">
            <div className="warning-icon">⚠️</div>
            <p className="error">Some requirements are not yet met:</p>
            <ul className="requirements-list">
              {!userStatus.old_photo_status.valid && (
                <li>❌ At least 16 valid old photos (with at least 1 older than 1 year) are required</li>
              )}
              {!userStatus.present_photos_status.all_uploaded && (
                <li>❌ All 6 present photos required</li>
              )}
              {userStatus.present_photos_status.all_uploaded && !userStatus.present_photos_status.all_valid && (
                <li>❌ At least 3 photos must be without glasses (Currently: {userStatus.present_photos_status.without_glasses_count} / 3)</li>
              )}
              {!isAssignedToPostB && !postAValid && (
                <li>❌ Valid Post Variation A photo required</li>
              )}
              {userStatus.post_b_status?.photos?.length === 0 && (
                <li>❌ Valid Post Variation B photo required</li>
              )}
            </ul>
          {adminRole !== 'viewer' && (
            <button onClick={handleBackToUpload} className="continue-btn">
              Continue Upload Process
            </button>
          )}
          </div>
        )}
      </div>
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3>Edit Profile</h3>
            <form onSubmit={handleEditSubmit} className="registration-form" style={{ marginTop: '20px' }}>
              <div className="form-group">
                <label>Name:</label>
                <input type="text" name="name" value={editFormData.name || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input type="email" name="email" value={editFormData.email || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Mobile Number:</label>
                <input type="tel" name="mobile_no" value={editFormData.mobile_no || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Date of Birth:</label>
                <input type="date" name="dob" value={editFormData.dob || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Android Version:</label>
                <input type="text" name="android_version" value={editFormData.android_version || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Country:</label>
                <input type="text" name="country" value={editFormData.country || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Gender:</label>
                <select name="gender" value={editFormData.gender || ''} onChange={handleEditChange} required>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ethnicity:</label>
                <input type="text" name="ethnicity" value={editFormData.ethnicity || ''} onChange={handleEditChange} required />
              </div>
              <div className="modal-actions">
                <button type="submit" className="upload-btn">Save Changes</button>
                <button type="button" onClick={() => setShowEditModal(false)} className="delete-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)} style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: 'white', fontSize: '3rem', cursor: 'pointer' }}>&times;</button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={lightboxImage} alt="Fullscreen" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
